"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import type { Referral, ReferralStatus } from "@/app/_module/services/referralService";

const STATUS_CONFIG: Record<ReferralStatus, { label: string; className: string }> = {
  clicked: { label: "Clicked", className: "bg-gray-100 text-gray-700" },
  signed_up: { label: "Signed Up", className: "bg-blue-100 text-blue-700" },
  trial: { label: "Trial", className: "bg-amber-100 text-amber-700" },
  converted: { label: "Converted", className: "bg-green-100 text-green-700" },
  churned: { label: "Churned", className: "bg-red-100 text-red-700" },
};

interface RewardsHistoryTableProps {
  referrals: Referral[];
  total: number;
}

export default function RewardsHistoryTable({
  referrals,
  total,
}: RewardsHistoryTableProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Referral History{" "}
          <span className="text-sm font-normal text-gray-500">({total})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {referrals.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            No referrals yet. Share your link to get started!
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-gray-600">User</th>
                  <th className="pb-2 font-medium text-gray-600">Status</th>
                  <th className="pb-2 font-medium text-gray-600">Date</th>
                  <th className="pb-2 font-medium text-gray-600">Reward</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((ref) => {
                  const config = STATUS_CONFIG[ref.status] || STATUS_CONFIG.clicked;
                  const name = ref.referredUser
                    ? `${ref.referredUser.firstName} ${ref.referredUser.lastName}`
                    : "Anonymous";
                  const date =
                    ref.convertedAt || ref.signedUpAt || ref.clickedAt || ref.createdAt;

                  return (
                    <tr key={ref.id} className="border-b last:border-0">
                      <td className="py-3">
                        <div>
                          <p className="font-medium text-gray-900">{name}</p>
                          {ref.referredUser?.email && (
                            <p className="text-xs text-gray-500">
                              {ref.referredUser.email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge variant="secondary" className={config.className}>
                          {config.label}
                        </Badge>
                      </td>
                      <td className="py-3 text-gray-600">
                        {new Date(date).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        {ref.status === "converted" ? (
                          <span className="text-green-600 font-medium text-xs">
                            +1 Free Month
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
